import { useNavigate } from 'react-router';
import { useCallback, useEffect, useEffectEvent, useMemo, useState } from 'react';
import { useTenant } from '../contexts/TenantContext';
import { useAuth } from '../contexts/AuthContext';
import { getLastTenantSlug, setLastTenantSlug } from '../utils/lastTenant';
import { ChannelIcon } from '../components/inbox/ChannelIcon';
import type { Tenant, AllowedPage, TenantBot } from '../types/dashboard';
import { config } from '../config';
import { ROLE_BADGE_KEY, roleBadge } from '../lib/tenantAccess';
import { useTranslation } from 'react-i18next';

/**
 * Workspace picker — landing for first-time visits, multi-tenant operators,
 * and explicit `/tenants` URL.
 *
 * **Visual model:** compact list rows, recency-sorted, "Resume last
 * session" hero on top. Replaces the prior card-grid + hero icon design
 * which was scenery-heavy and slowed daily ops.
 *
 * **Auto-routing:** zero accessible workspaces → /no-access. Single
 * workspace with pages → /:slug/inbox (skip the picker — there's
 * nothing to pick). Single workspace with bots only → /bots (flow
 * builder, not in scope for the inbox surface). Anything else falls
 * through to the picker.
 *
 * **Per-tenant action label:**
 *   - tenant has pages → "Open Inbox" (→ /:slug/inbox)
 *   - tenant has bots only → "Open Flow Builder" (→ /bots)
 *   - tenant has neither → row disabled, "No access configured"
 *
 * The decision *how* to enter a workspace (inbox vs page-management vs
 * builder) lives inside that workspace, not here. The picker only picks
 * the workspace and lands in the most useful default surface.
 */

interface WorkspaceRowData {
  tenant: Tenant;
  pages: AllowedPage[];
  bots: TenantBot[];
  hasPages: boolean;
  hasBotsOnly: boolean;
  hasNoAccess: boolean;
}

function buildChannelSummary(pages: AllowedPage[]): string[] {
  const seen = new Set<string>();
  for (const p of pages) seen.add(p.channel);
  return Array.from(seen).sort();
}

export function TenantSelector() {
  const navigate = useNavigate();
  const { tenants: allTenants, tenantPages, tenantBots, loading, contextTenant } = useTenant();
  const { userInfo, isGlobalAdmin, access } = useAuth();
  const { t } = useTranslation();
  // A global admin's context tenant is not "theirs" — it is reachable from the search below.
  const tenants = useMemo(
    () => allTenants.filter((t) => t.id !== contextTenant?.id),
    [allTenants, contextTenant],
  );
  const [searchQuery, setSearchQuery] = useState('');

  const navigateTo = useEffectEvent((path: string, options?: { replace?: boolean }) => {
    navigate(path, options);
  });

  // Auto-routing: skip the picker when there's only one realistic option.
  useEffect(() => {
    // Global admins always get the picker (they can open any workspace from the search).
    if (loading || isGlobalAdmin) return;

    const keysWithPages = Object.keys(tenantPages).filter(
      (tenantId) => tenantPages[tenantId].length > 0,
    );
    const keysWithBots = Object.keys(tenantBots || {}).filter(
      (tenantId) => (tenantBots?.[tenantId]?.length ?? 0) > 0,
    );

    if (keysWithPages.length === 0 && keysWithBots.length === 0) {
      navigateTo('/no-access', { replace: true });
      return;
    }

    if (keysWithPages.length === 1) {
      const tenant = tenants?.find((t) => t.id === keysWithPages[0]);
      if (tenant) {
        setLastTenantSlug(tenant.slug);
        navigateTo(`/${tenant.slug}/inbox`, { replace: true });
      }
    }
    // Bot-only workspaces stay on the picker: the flow builder lives in the hub app now
    // (the former `/bots` route no longer exists here).
  }, [loading, isGlobalAdmin, tenants, tenantPages, tenantBots]);

  // ── Build row data + recency sort ──────────────────────────────────
  const rows = useMemo<WorkspaceRowData[]>(() => {
    if (!tenants) return [];
    const lastSlug = getLastTenantSlug();
    return tenants
      .map<WorkspaceRowData>((tenant) => {
        const pages = tenantPages[tenant.id] || [];
        const bots = tenantBots?.[tenant.id] || [];
        return {
          tenant,
          pages,
          bots,
          hasPages: pages.length > 0,
          hasBotsOnly: pages.length === 0 && bots.length > 0,
          hasNoAccess: pages.length === 0 && bots.length === 0,
        };
      })
      .sort((a, b) => {
        // 1. Last-used tenant first (resume target). 2. Tenants with pages
        // before bot-only. 3. No-access tenants last. 4. Alphabetical within
        // each bucket.
        const aLast = a.tenant.slug === lastSlug ? 0 : 1;
        const bLast = b.tenant.slug === lastSlug ? 0 : 1;
        if (aLast !== bLast) return aLast - bLast;
        const aBucket = a.hasPages ? 0 : a.hasBotsOnly ? 1 : 2;
        const bBucket = b.hasPages ? 0 : b.hasBotsOnly ? 1 : 2;
        if (aBucket !== bBucket) return aBucket - bBucket;
        return a.tenant.name.localeCompare(b.tenant.name);
      });
  }, [tenants, tenantPages, tenantBots]);

  // Resume hero — the last-used tenant if it's still accessible.
  const resumeRow = useMemo(() => {
    const lastSlug = getLastTenantSlug();
    if (!lastSlug) return null;
    return rows.find((r) => r.tenant.slug === lastSlug && r.hasPages) ?? null;
  }, [rows]);

  // Search filter — case-insensitive on name or slug.
  const filteredRows = useMemo(() => {
    if (!searchQuery.trim()) return rows;
    const q = searchQuery.toLowerCase().trim();
    return rows.filter(
      (r) =>
        r.tenant.name.toLowerCase().includes(q) ||
        r.tenant.slug.toLowerCase().includes(q),
    );
  }, [rows, searchQuery]);

  const accessibleRows = filteredRows.filter((r) => !r.hasNoAccess);
  const noAccessRows = filteredRows.filter((r) => r.hasNoAccess);

  // ── Handlers ───────────────────────────────────────────────────────
  const openInbox = useCallback(
    (tenant: Tenant) => {
      setLastTenantSlug(tenant.slug);
      navigate(`/${tenant.slug}/inbox`);
    },
    [navigate],
  );

  const userName = (userInfo() as { name?: string }).name || t('common.operator');

  // ── Render ─────────────────────────────────────────────────────────
  return (
    <div className="flex flex-col h-full w-full bg-canvas overflow-y-auto custom-scrollbar">
      <div className="w-full max-w-3xl mx-auto px-4 sm:px-6 py-6 sm:py-8 lg:py-10">
        {/* Compact header — single line, no hero icon */}
        <header className="mb-6">
          <h1 className="text-xl sm:text-2xl font-semibold text-ink">
            {t('workspaces.welcome', { name: userName })}
          </h1>
          <p className="text-sm text-muted mt-1">
            {loading
              ? t('workspaces.loadingYours')
              : rows.length === 0
              ? isGlobalAdmin
                ? t('workspaces.noneSearchBelow')
                : t('workspaces.none')
              : rows.length === 1
              ? t('workspaces.one')
              : t('workspaces.choose', { n: rows.length })}
          </p>
        </header>

        {/* Resume hero — only when there's a valid last-tenant */}
        {resumeRow && (
          <button
            onClick={() => openInbox(resumeRow.tenant)}
            className="group w-full mb-6 px-5 py-4 bg-surface border-2 border-line rounded-xl shadow-card hover:border-primary hover:shadow-md transition-all text-left flex items-center justify-between gap-4"
          >
            <div className="flex items-center gap-3 min-w-0">
              <div className="flex-shrink-0 w-10 h-10 rounded-lg bg-tint flex items-center justify-center shadow-card">
                <svg
                  className="w-5 h-5 text-on-tint"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                  aria-hidden="true"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M14 5l7 7m0 0l-7 7m7-7H3"
                  />
                </svg>
              </div>
              <div className="min-w-0">
                <p className="text-xs uppercase tracking-wide font-semibold text-primary">
                  {t('workspaces.resume')}
                </p>
                <p className="text-base font-semibold text-ink truncate">
                  {resumeRow.tenant.name}
                </p>
                <p className="text-xs text-muted truncate">
                  {t('common.pages', { count: resumeRow.pages.length })} ·{' '}
                  {buildChannelSummary(resumeRow.pages).join(', ')}
                </p>
              </div>
            </div>
            <span className="text-sm font-medium text-primary group-hover:text-primary flex-shrink-0">
              {t('workspaces.openInbox')} →
            </span>
          </button>
        )}

        {/* Search */}
        {rows.length > 5 && (
          <div className="mb-4">
            <div className="relative">
              <svg
                className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted pointer-events-none"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
                aria-hidden="true"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"
                />
              </svg>
              <input
                type="text"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                placeholder={t('workspaces.searchPlaceholder')}
                className="w-full pl-10 pr-3 py-2 text-sm border border-line rounded-lg bg-surface focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent"
              />
            </div>
          </div>
        )}

        {/* Loading state */}
        {loading && rows.length === 0 && (
          <div className="py-12 text-center">
            <div className="inline-block animate-spin rounded-full h-8 w-8 border-2 border-primary border-t-transparent mb-3" />
            <p className="text-sm text-muted">{t('workspaces.loading')}</p>
          </div>
        )}

        {/* Accessible workspaces */}
        {!loading && accessibleRows.length > 0 && (
          <section>
            <h2 className="text-xs uppercase tracking-wide font-semibold text-muted mb-2">
              {t('workspaces.yours')}
            </h2>
            <ul className="space-y-1 mb-6">
              {accessibleRows.map((r) => (
                <WorkspaceRow
                  key={r.tenant.id}
                  data={r}
                  badge={access.roles[r.tenant.id] ? t(ROLE_BADGE_KEY[roleBadge({ ...access, isGlobalAdmin: false }, r.tenant.id)!]) : null}
                  onOpenInbox={() => openInbox(r.tenant)}
                />
              ))}
            </ul>
          </section>
        )}

        {/* Empty search result */}
        {!loading && rows.length > 0 && filteredRows.length === 0 && (
          <div className="py-8 text-center">
            <p className="text-sm text-muted">
              {t('workspaces.noMatch', { q: searchQuery })}
            </p>
            <button
              onClick={() => setSearchQuery('')}
              className="mt-2 text-sm text-primary hover:text-primary"
            >
              {t('workspaces.clearSearch')}
            </button>
          </div>
        )}

        {/* Global admins: every workspace, server-side search */}
        {isGlobalAdmin && <AllWorkspacesSearch onOpen={openInbox} />}

        {/* No-access workspaces — collapsed by default to reduce noise */}
        {!loading && noAccessRows.length > 0 && (
          <NoAccessSection rows={noAccessRows} />
        )}
      </div>
    </div>
  );
}

/**
 * Single workspace row. Variants:
 *   - hasPages → primary "Open inbox →" action
 *   - hasBotsOnly → "Open builder →" action with muted accent
 *   - hasNoAccess → disabled row, "No access configured" muted text
 */
function WorkspaceRow({
  data,
  badge,
  onOpenInbox,
}: {
  data: WorkspaceRowData;
  badge: string | null;
  onOpenInbox: () => void;
}) {
  const { tenant, pages, bots, hasPages, hasBotsOnly } = data;
  const { t } = useTranslation();
  const channels = buildChannelSummary(pages);
  const action = hasPages ? onOpenInbox : undefined;
  const actionLabel = hasPages ? t('workspaces.openInbox') : null;
  const disabled = !action;

  return (
    <li>
      <button
        onClick={action}
        disabled={disabled}
        className={`group w-full px-4 py-3 rounded-lg border transition-all flex items-center justify-between gap-3 text-left ${
          disabled
            ? 'bg-surface-2 border-line cursor-not-allowed'
            : 'bg-surface border-line hover:border-line hover:bg-surface-2 hover:shadow-card'
        }`}
      >
        <div className="min-w-0 flex-1">
          <p
            className={`text-sm font-medium truncate ${
              disabled ? 'text-muted' : 'text-ink'
            }`}
          >
            {tenant.name}
            {badge && (
              <span className="ml-2 align-middle text-xs font-medium uppercase tracking-wide text-muted bg-surface-2 rounded px-1.5 py-0.5">
                {badge}
              </span>
            )}
          </p>
          <div className="flex items-center gap-2 mt-0.5 text-xs text-muted">
            {hasPages ? (
              <>
                <span>
                  {t('common.pages', { count: pages.length })}
                </span>
                <span className="text-muted">·</span>
                <span className="inline-flex items-center gap-1">
                  {channels.map((c) => (
                    <ChannelIcon key={c} channel={c} className="w-3 h-3" />
                  ))}
                  <span>{channels.join(', ')}</span>
                </span>
              </>
            ) : hasBotsOnly ? (
              <span>
                {t('workspaces.bots', { count: bots.length })}
              </span>
            ) : (
              <span>{t('workspaces.noAccessConfigured')}</span>
            )}
          </div>
        </div>
        {actionLabel && (
          <span
            className={`text-sm font-medium flex-shrink-0 ${
              hasPages ? 'text-primary group-hover:text-primary' : 'text-muted group-hover:text-ink'
            }`}
          >
            {actionLabel} →
          </span>
        )}
      </button>
    </li>
  );
}

/**
 * Collapsed-by-default "Without access" section. Operators don't usually
 * care about these rows — show as a small toggle so it doesn't dominate
 * the page.
 */
function NoAccessSection({ rows }: { rows: WorkspaceRowData[] }) {
  const [expanded, setExpanded] = useState(false);
  const { t } = useTranslation();
  return (
    <section>
      <button
        onClick={() => setExpanded((e) => !e)}
        className="text-xs uppercase tracking-wide font-semibold text-muted hover:text-muted mb-2 inline-flex items-center gap-1"
      >
        <svg
          className={`w-3 h-3 transition-transform ${expanded ? 'rotate-90' : ''}`}
          fill="none"
          stroke="currentColor"
          viewBox="0 0 24 24"
          aria-hidden="true"
        >
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
        </svg>
        {t('workspaces.withoutAccess', { n: rows.length })}
      </button>
      {expanded && (
        <ul className="space-y-1">
          {rows.map((r) => (
            <li
              key={r.tenant.id}
              className="px-4 py-2 bg-surface-2 border border-line rounded text-sm text-muted"
            >
              {t('workspaces.noPagesOrBots', { name: r.tenant.name })}
            </li>
          ))}
        </ul>
      )}
    </section>
  );
}

/**
 * Global admins: search every workspace (GET /api/operator/tenants → SSO admin search).
 * Opening one makes it the context tenant (useResolvedTenant); access there is audited server-side.
 */
function AllWorkspacesSearch({ onOpen }: { onOpen: (tenant: Tenant) => void }) {
  const { apiFetch } = useAuth();
  const { t } = useTranslation();
  const [q, setQ] = useState('');
  const [state, setState] = useState<
    | { status: 'loading' }
    | { status: 'error' }
    | { status: 'ready'; items: Array<{ id: string; name: string; slug: string | null; role: string }>; total: number }
  >({ status: 'loading' });

  useEffect(() => {
    let cancelled = false;
    const timer = setTimeout(async () => {
      setState({ status: 'loading' });
      try {
        const resp = await apiFetch(
          `${config.apiServer}/api/operator/tenants?q=${encodeURIComponent(q.trim())}&take=20`,
        );
        if (!resp.ok) throw new Error(String(resp.status));
        const data = await resp.json();
        if (!cancelled) setState({ status: 'ready', items: data?.items ?? [], total: data?.total ?? 0 });
      } catch {
        if (!cancelled) setState({ status: 'error' });
      }
    }, 300);
    return () => {
      cancelled = true;
      clearTimeout(timer);
    };
  }, [q, apiFetch]);

  return (
    <section className="mb-6">
      <h2 className="text-xs uppercase tracking-wide font-semibold text-muted mb-2">
        {t('workspaces.allTitle')} <span className="normal-case font-normal text-muted">{t('workspaces.allNote')}</span>
      </h2>
      <input
        type="text"
        value={q}
        onChange={(e) => setQ(e.target.value)}
        placeholder={t('workspaces.allSearchPlaceholder')}
        aria-label={t('workspaces.allSearchLabel')}
        className="w-full mb-2 px-3 py-2 text-sm border border-line rounded-lg bg-surface focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent"
      />
      {state.status === 'loading' && <p className="text-sm text-muted py-3">{t('workspaces.searching')}</p>}
      {state.status === 'error' && (
        <p className="text-sm text-danger py-3">{t('workspaces.searchUnavailable')}</p>
      )}
      {state.status === 'ready' && state.items.length === 0 && (
        <p className="text-sm text-muted py-3">
          {q.trim() ? t('workspaces.noMatch', { q: q.trim() }) : t('workspaces.noneFound')}
        </p>
      )}
      {state.status === 'ready' && state.items.length > 0 && (
        <>
          <ul className="space-y-1">
            {state.items.map((ws) => (
              <li key={ws.id}>
                <button
                  type="button"
                  disabled={!ws.slug}
                  onClick={() => ws.slug && onOpen({ id: ws.id, name: ws.name, slug: ws.slug })}
                  className="group w-full px-4 py-2.5 rounded-lg border bg-surface border-line hover:border-line hover:bg-surface-2 transition-all flex items-center justify-between gap-3 text-left disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  <span className="min-w-0">
                    <span className="block text-sm font-medium text-ink truncate">{ws.name}</span>
                    <span className="block text-xs text-muted truncate">{ws.slug ?? t('workspaces.noSlug')}</span>
                  </span>
                  <span className="text-sm font-medium text-primary group-hover:text-primary flex-shrink-0">{t('workspaces.openInbox')} →</span>
                </button>
              </li>
            ))}
          </ul>
          {state.total > state.items.length && (
            <p className="text-xs text-muted mt-2">
              {t('workspaces.showing', { shown: state.items.length, total: state.total })}
            </p>
          )}
        </>
      )}
    </section>
  );
}
